import React, { useState, useMemo } from 'react';
import { InstagramLogo, TiktokLogo, Copy, Check, ShareNetwork, DownloadSimple } from '@phosphor-icons/react';

/**
 * Guest-facing share loop that fires after a capture made with a face-tracked
 * AR mask. Pre-writes a caption tagging the event + "Powered by eventdrop.co"
 * and opens the platform upload screen with the caption already copied.
 *
 * Props:
 *   eventName      : string — host-event name (e.g. "Ava's 18th")
 *   maskName       : string — mask label shown in caption (e.g. "Dog")
 *   maskEmoji      : string — mask emoji for caption flavour
 *   mediaUrl       : string — blob/data URL of the rendered photo/video/boomerang (download)
 *   mediaKind      : 'photo' | 'video' | 'boomerang' — file-extension hint
 *   onCopied       : optional callback after caption copied
 */
const LANDING = 'https://eventdrop.co';

const buildCaption = (eventName, maskName, maskEmoji) => {
  const ev = (eventName || 'the event').trim();
  const mask = maskName || 'AR mask';
  const emoji = maskEmoji || '✨';
  return `${emoji} ${mask} mode at ${ev} — caught with the AR booth.

Try it yourself at your next event → ${LANDING}

Powered by eventdrop.co`;
};

const downloadBlob = async (mediaUrl, filename) => {
  try {
    const res = await fetch(mediaUrl);
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1500);
    return true;
  } catch {
    return false;
  }
};

const ArShareCard = ({ eventName, maskName, maskEmoji, mediaUrl, mediaKind = 'photo' }) => {
  const caption = useMemo(
    () => buildCaption(eventName, maskName, maskEmoji),
    [eventName, maskName, maskEmoji]
  );
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const ext = mediaKind === 'photo' ? 'png' : 'webm';
  const fileBase = `eventdrop-${(maskName || 'mask').toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`;

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      return true;
    } catch {
      return false;
    }
  };

  const saveMedia = async () => {
    if (!mediaUrl) return false;
    const ok = await downloadBlob(mediaUrl, `${fileBase}.${ext}`);
    if (ok) {
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    }
    return ok;
  };

  // Try native sheet with file attachment — best UX on iOS/Android for sending straight to IG/TikTok.
  const nativeShare = async () => {
    await copyCaption();
    if (!navigator.share) return;
    try {
      let files = undefined;
      if (mediaUrl) {
        try {
          const res = await fetch(mediaUrl);
          const blob = await res.blob();
          const mime = mediaKind === 'photo' ? 'image/png' : 'video/webm';
          const file = new File([blob], `${fileBase}.${ext}`, { type: mime });
          if (navigator.canShare && navigator.canShare({ files: [file] })) files = [file];
        } catch {}
      }
      await navigator.share({
        title: `${eventName || 'Event'} — EventDrop`,
        text: caption,
        url: LANDING,
        ...(files ? { files } : {}),
      });
    } catch { /* user dismissed — fine */ }
  };

  const shareInstagram = async () => {
    await copyCaption();
    await saveMedia();
    const ua = navigator.userAgent || '';
    if (/iphone|ipad|ipod/i.test(ua)) {
      window.location.href = 'instagram://story-camera';
      setTimeout(() => { window.location.href = 'https://www.instagram.com/'; }, 1200);
    } else if (/android/i.test(ua)) {
      window.location.href = 'https://www.instagram.com/';
    } else {
      window.open('https://www.instagram.com/', '_blank');
    }
  };

  const shareTikTok = async () => {
    await copyCaption();
    await saveMedia();
    const ua = navigator.userAgent || '';
    if (/android|iphone|ipad|ipod/i.test(ua)) {
      window.location.href = 'snssdk1233://upload';
      setTimeout(() => { window.location.href = 'https://www.tiktok.com/upload'; }, 1200);
    } else {
      window.open('https://www.tiktok.com/upload', '_blank');
    }
  };

  return (
    <div
      className="rounded-2xl p-4 border relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(225,48,108,0.12), rgba(184,151,98,0.08))',
        borderColor: 'rgba(225,48,108,0.28)',
        animation: 'arShareIn 0.5s cubic-bezier(0.22,1,0.36,1) 0.2s both',
      }}
      data-testid="ar-share-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{maskEmoji || '✨'}</span>
        <h4 className="text-white font-bold text-sm">Post this {maskName || 'mask'} shot</h4>
        <span className="ml-auto text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/10 text-white/70 font-bold">
          1 tap
        </span>
      </div>
      <p className="text-white/60 text-[11px] leading-snug mb-3">
        Caption pre-written with <span className="text-white font-semibold">Powered by eventdrop.co</span> — we'll copy it and open your app.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <button
          onClick={shareInstagram}
          className="inline-flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-bold text-white transition-all active:scale-[0.97]"
          style={{ background: 'linear-gradient(135deg, #E1306C, #FCAF45)' }}
          data-testid="ar-share-instagram"
        >
          <InstagramLogo size={14} weight="bold" />
          Instagram
        </button>
        <button
          onClick={shareTikTok}
          className="inline-flex items-center justify-center gap-1.5 h-10 rounded-xl text-xs font-bold text-white bg-black border border-white/20 transition-all active:scale-[0.97]"
          data-testid="ar-share-tiktok"
        >
          <TiktokLogo size={14} weight="bold" />
          TikTok
        </button>
      </div>

      <div className="flex gap-2">
        <button
          onClick={copyCaption}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[11px] font-semibold text-gray-200 bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
          data-testid="ar-share-copy-caption"
        >
          {copied ? <Check size={12} weight="bold" className="text-green-400" /> : <Copy size={12} weight="bold" />}
          {copied ? 'Copied!' : 'Copy caption'}
        </button>
        <button
          onClick={saveMedia}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[11px] font-semibold text-gray-200 bg-white/5 border border-white/10 hover:bg-white/10 transition-all"
          data-testid="ar-share-save"
        >
          {saved ? <Check size={12} weight="bold" className="text-green-400" /> : <DownloadSimple size={12} weight="bold" />}
          {saved ? 'Saved' : 'Save file'}
        </button>
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={nativeShare}
            className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-lg text-[11px] font-bold text-white transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="ar-share-native"
          >
            <ShareNetwork size={12} weight="bold" />
            Share…
          </button>
        )}
      </div>

      <p className="text-white/30 text-[9px] mt-2 text-center">
        Paste the caption after attaching the saved file — Powered by eventdrop.co
      </p>
      <style>{`
        @keyframes arShareIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default ArShareCard;
